import ICoBrowseDto, { initialCoBrowseObject } from "../interface/ICoBrowseDto";
import IMessage from "../interface/IMessage";
import initializeGenesysSdk from "./initializeGenesysSdk";
import { resetUploadFileState } from "./uploadFile";
import { v4 as uuidv4 } from "uuid";
import debugLog from "../utils/debugLog";
import { reportAppError } from "../utils/reportAppError";

// Signals the outcome of loading the Genesys SDK to the caller (UI layer).
export type GenesysLoadStatus = "ready" | "error";

// If MessagingService.ready does not fire within this window after init, the
// load is treated as failed (e.g. the SDK script loaded but never became ready).
const SDK_READY_TIMEOUT_MS = 15000;

let backendInitialized = false;
let messagingSubscriptionsRegistered = false;
let cobrowseSubscriptionsRegistered = false;
let sdkReady = false;
let onReceiveMessageSubscribed = false;
let botTypingTimeout: ReturnType<typeof setTimeout> | null = null;
let readyTimeout: ReturnType<typeof setTimeout> | null = null;

const clearReadyTimeout = (): void => {
  if (readyTimeout !== null) {
    clearTimeout(readyTimeout);
    readyTimeout = null;
  }
};

export const isBackendReady = (): boolean => sdkReady;
export const isOnReceiveMessageSubscribed = (): boolean =>
  onReceiveMessageSubscribed;
export const markOnReceiveMessageSubscribed = (): void => {
  onReceiveMessageSubscribed = true;
};

export const disconnectFromBackend = (): void => {
  backendInitialized = false;
  messagingSubscriptionsRegistered = false;
  cobrowseSubscriptionsRegistered = false;
  sdkReady = false;
  onReceiveMessageSubscribed = false;
  clearReadyTimeout();
  resetUploadFileState();
};

export const connectToBackend = async (
  setBotIsTyping: React.Dispatch<React.SetStateAction<boolean>>,
  setCoBrowseOffer: React.Dispatch<React.SetStateAction<ICoBrowseDto>>,
  updateMessageList: (message: IMessage, saveToLocalStorage: boolean) => void,
  getDeploymentId: () => string,
  onStatus?: (status: GenesysLoadStatus) => void,
) => {
  if (backendInitialized) return;

  const updateCoBrowseOffer = (
    updater: (prevState: ICoBrowseDto) => ICoBrowseDto,
  ) => {
    setCoBrowseOffer((prevState: ICoBrowseDto) => updater(prevState));
  };

  // Report a failed load exactly once (script error OR ready timeout).
  let loadSettled = false;
  const reportLoadFailure = (scope: string): void => {
    if (loadSettled) return;
    loadSettled = true;
    clearReadyTimeout();
    backendInitialized = false;
    reportAppError({ scope });
    onStatus?.("error");
  };

  try {
    backendInitialized = true;

    // Initialize Genesys SDK. A failed script load (offline, CSP, blocked
    // domains) surfaces via onError so we can show an error state.
    initializeGenesysSdk(getDeploymentId, () =>
      reportLoadFailure("backendConnector.scriptLoadError"),
    );

    // Guard against the SDK loading but never becoming ready.
    readyTimeout = setTimeout(
      () => reportLoadFailure("backendConnector.readyTimeout"),
      SDK_READY_TIMEOUT_MS,
    );

    // Subscribe to the event when the co-browsing offer is expired
    Genesys(
      "subscribe",
      "MessagingService.cobrowseOfferExpired",
      function ({ data }: any) {
        debugLog("cobrowseOfferExpired", data);
        updateCoBrowseOffer((prevState: ICoBrowseDto) => ({
          ...prevState,
          openOffer: false,
          loading: false,
        }));

        updateMessageList(
          {
            id: uuidv4(),
            text: "Bildschirmfreigabe ist abgelaufen.",
            sender: "system",
            timeStamp: new Date().toISOString(),
            content: null,
            sending: false,
            agent: {
              avatar: "",
              name: "",
              nickname: "",
            },
          },
          true,
        );
      },
    );

    // Subscribe to the event when the messaging service is ready
    Genesys("subscribe", "MessagingService.ready", () => {
      if (messagingSubscriptionsRegistered) return;
      messagingSubscriptionsRegistered = true;
      sdkReady = true;
      loadSettled = true;
      clearReadyTimeout();
      onStatus?.("ready");

      debugLog("MessagingService is ready");

      // Subscribe to messageAdded event
      Genesys("subscribe", "MessagingService.messageAdded", (message: any) => {
        debugLog("Neue Nachricht hinzugefügt:", message);
        if (message.sender && message.sender.name) {
          const agentenName = message.sender.name;
          debugLog("Agentenname:", agentenName);
        }
      });

      // Subscribe to messageDelivered event
      Genesys(
        "subscribe",
        "MessagingService.messageDelivered",
        (message: any) => {
          debugLog("Nachricht zugestellt:", message);
        },
      );

      // Subscribe to messageRead event
      Genesys("subscribe", "MessagingService.messageRead", (message: any) => {
        debugLog("Nachricht gelesen:", message);
      });

      // Subscribe to offline event
      Genesys("subscribe", "MessagingService.offline", function () {
        debugLog("MessagingService is offline");
      });

      // Subscribe to reconnecting event
      Genesys("subscribe", "MessagingService.reconnecting", function () {
        debugLog("MessagingService is reconnecting");
      });

      // Subscribe to reconnected event
      Genesys("subscribe", "MessagingService.reconnected", function () {
        debugLog("MessagingService is reconnected");
      });

      // Subscribe to readOnlyConversation event
      Genesys(
        "subscribe",
        "MessagingService.readOnlyConversation",
        function ({ data }: any) {
          debugLog("MessagingService is read-only", data);
        },
      );

      // Subscribe to conversationReset event
      Genesys(
        "subscribe",
        "MessagingService.conversationReset",
        function ({ data }: any) {
          debugLog(data);
        },
      );

      // Subscribe to conversationDisconnected event and reset the conversation when it occurs
      Genesys(
        "subscribe",
        "MessagingService.conversationDisconnected",
        ({ data }: any) => {
          debugLog("Conversation disconnected:", data);
          try {
            Genesys(
              "command",
              "MessagingService.resetConversation",
              {},
              function () {
                updateMessageList(
                  {
                    id: uuidv4(),
                    text: "Das Gespräch wurde beendet.",
                    sender: "system",
                    timeStamp: new Date().toISOString(),
                    content: null,
                    sending: false,
                    agent: {
                      avatar: "",
                      name: "",
                      nickname: "",
                    },
                  },
                  true,
                );
              },
              function () {
                debugLog("Conversation NOT restarted. Check read-only mode.");
              },
            );
          } catch (e) {
            debugLog("resetConversationError: ", e);
          }
        },
      );

      // Clear the agent typing event timeout when the end user begins typing or has an attachment before the
      // typing indicator timeout value(5 seconds) is up.
      Genesys("subscribe", "MessagingService.clearTypingTimeout", () => {
        if (botTypingTimeout !== null) {
          clearTimeout(botTypingTimeout);
          botTypingTimeout = null;
        }

        setBotIsTyping(false);
        debugLog("Typing timeout cleared");
      });

      // Published when file upload is in progress. It also provides the percentage of content uploaded in number.
      Genesys(
        "subscribe",
        "MessagingService.uploading",
        function ({ data }: any) {
          debugLog(data);
        },
      );

      // Subscribe to the event when the bot is typing
      // set the botIsTyping state to true and after the duration to false
      Genesys(
        "subscribe",
        "MessagingService.typingReceived",
        ({ data }: any) => {
          debugLog("Bot is typing:", data);

          const duration = Number(data?.typing?.duration);
          const timeoutDuration =
            Number.isFinite(duration) && duration > 0 ? duration : 5000;

          if (botTypingTimeout !== null) {
            clearTimeout(botTypingTimeout);
          }

          setBotIsTyping(true);

          botTypingTimeout = setTimeout(() => {
            setBotIsTyping(false);
            botTypingTimeout = null;
          }, timeoutDuration);
        },
        (error: any) => {
          reportAppError({
            scope: "backendConnector.subscribe.typingReceived",
            error,
          });
        },
      );

      // Subscribe to the event when the conversation is ready
      Genesys("subscribe", "Conversations.ready", (data: any) => {
        debugLog("Conversation ready:", data);
      });

      // Subscribe to the event when there is an error with the messaging service
      Genesys("subscribe", "MessagingService.error", function ({ data }: any) {
        debugLog("MessagingServiceError: ", data);
      });

      // Subscribe to the event when the bot sends a co-browsing offer
      Genesys(
        "subscribe",
        "MessagingService.cobrowseOffer",
        ({ data }: any) => {
          updateCoBrowseOffer((prevState: ICoBrowseDto) => ({
            ...prevState,
            session: data,
          }));

          updateMessageList(
            {
              id: uuidv4(),
              text: "Bildschirmfreigabe wird angeboten.",
              sender: "system",
              timeStamp: new Date().toISOString(),
              content: null,
              sending: false,
              agent: {
                avatar: "",
                name: "",
                nickname: "",
              },
            },
            true,
          );
        },
        (error: any) => {
          reportAppError({
            scope: "backendConnector.subscribe.cobrowseOffer",
            error,
          });
        },
      );

      // Subscribe to the event when the co-browsing offer is accepted
      Genesys(
        "subscribe",
        "MessagingService.cobrowseOfferAccepted",
        function ({ data }: any) {
          debugLog("cobrowseOfferAccepted", data);
        },
      );

      // start Co-Browsing
      Genesys("subscribe", "CobrowseService.ready", (e: any) => {
        if (cobrowseSubscriptionsRegistered) return;
        cobrowseSubscriptionsRegistered = true;

        debugLog("CobrowseService is ready", e);

        // Subscribe to the event when the co-browsing offer is declined
        Genesys("subscribe", "MessagingService.cobrowseOfferRejected", () => {
          debugLog("Co-browse session politely declined");
          setCoBrowseOffer({ ...initialCoBrowseObject });

          updateMessageList(
            {
              id: uuidv4(),
              text: "Bildschirmfreigabe wurde abgelehnt.",
              sender: "system",
              timeStamp: new Date().toISOString(),
              content: null,
              sending: false,
              agent: {
                avatar: "",
                name: "",
                nickname: "",
              },
            },
            true,
          );
        });

        // Subscribe to the session start event
        Genesys("subscribe", "CobrowseService.sessionStarted", (e: any) => {
          debugLog("Co-browse session started", e);
          updateCoBrowseOffer((prevState: ICoBrowseDto) => ({
            ...prevState,
            session: {
              ...prevState.session,
              sessionJoinToken: e.data.joinCode,
              sessionId: e.data.sessionId,
            },
            openOffer: false,
            loading: false,
          }));
        });

        // Published when agent requests control over the page.
        Genesys("subscribe", "CobrowseService.controlRequested", (e: any) => {
          debugLog("Control requested", e);
          updateCoBrowseOffer((prevState: ICoBrowseDto) => ({
            ...prevState,
            control: {
              ...prevState.control,
              offer: true,
            },
          }));
        });

        // Published when agent requests navigation to a different URL.
        Genesys(
          "subscribe",
          "CobrowseService.navigationRequested",
          (e: any) => {
            debugLog("Navigation requested", e);
            updateCoBrowseOffer((prevState: ICoBrowseDto) => ({
              ...prevState,
              navigation: {
                ...prevState.navigation,
                offer: true,
                url: e.data.url,
              },
            }));
          },
        );

        // Subscribe to the session end event
        Genesys("subscribe", "CobrowseService.sessionEnded", (e: any) => {
          debugLog("Co-browse session ended.", e);
          setCoBrowseOffer({ ...initialCoBrowseObject });

          updateMessageList(
            {
              id: uuidv4(),
              text: "Bildschirmfreigabe wurde beendet.",
              sender: "system",
              timeStamp: new Date().toISOString(),
              content: null,
              sending: false,
              agent: {
                avatar: "",
                name: "",
                nickname: "",
              },
            },
            true,
          );
        });

        // Subscribe to the view requested event
        Genesys("subscribe", "CobrowseService.controlGranted", (e: any) => {
          debugLog("Control has been granted to the agent.", e);
          updateCoBrowseOffer((prevState: ICoBrowseDto) => ({
            ...prevState,
            control: {
              ...prevState.control,
              offer: false,
              allowControl: true,
            },
          }));
        });

        Genesys("subscribe", "CobrowseService.controlRevoked", () => {
          updateCoBrowseOffer((prevState: ICoBrowseDto) => ({
            ...prevState,
            control: {
              ...prevState.control,
              offer: false,
              allowControl: false,
            },
          }));
        });

        Genesys("subscribe", "CobrowseService.sessionStopped", function () {
          debugLog("Co-browse session stopped");
        });
      });
    });
  } catch (error) {
    clearReadyTimeout();
    backendInitialized = false;
    reportAppError({ scope: "backendConnector.initialize", error });
    onStatus?.("error");
  }
};
